import { Box, ButtonBase, createStyles, makeStyles, Modal, Slide } from '@material-ui/core'
import React, { useCallback, useEffect, useState } from 'react'
import { Button, Colors, DynamicMuiIcon, Typography } from 'src/components/atoms'
import { DocumentFolder } from 'src/fixtures/types/folder'

const useStyles = makeStyles(() => {
  return createStyles({
    modal: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: Colors.base.black,
      zIndex: 9999
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      width: '480px',
      backgroundColor: '#FFFFFF',
      borderRadius: '8px',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
      overflow: 'hidden'
    },
    header: {
      padding: '20px 24px',
      borderBottom: '1px solid #DADCE0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    title: {
      fontSize: '18px',
      fontWeight: 600,
      color: '#222B35'
    },
    closeButton: {
      width: '28px',
      height: '28px',
      border: 'none',
      background: 'transparent',
      color: '#707B89',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '4px',
      transition: 'background-color 0.2s',
      '&:hover': {
        backgroundColor: '#E3E7ED'
      }
    },
    body: {
      padding: '24px'
    },
    folderList: {
      maxHeight: '300px',
      overflowY: 'auto'
    },
    folderItem: {
      display: 'flex',
      alignItems: 'center',
      padding: '12px 16px',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      borderRadius: '4px',
      margin: '4px 0',
      '&:hover': {
        backgroundColor: '#F8FAFD'
      }
    },
    folderItemSelected: {
      backgroundColor: '#E8E7FC',
      '& $folderItemIcon': {
        color: Colors.accent.keyPurple.default
      },
      '& $folderItemName': {
        fontWeight: 500,
        color: Colors.accent.keyPurple.default
      }
    },
    folderItemIcon: {
      flexShrink: 0,
      marginRight: '12px',
      color: '#707B89'
    },
    folderItemName: {
      flex: 1,
      fontSize: '14px',
      color: '#222B35'
    },
    folderItemCount: {
      fontSize: '12px',
      color: '#707B89',
      marginLeft: '8px'
    },
    footer: {
      padding: '16px 24px',
      borderTop: '1px solid #DADCE0',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '12px'
    },
    emptyMessage: {
      fontSize: '14px',
      color: '#707B89',
      textAlign: 'center',
      padding: '24px'
    }
  })
})

export interface ModalSelectFolderProps {
  open: boolean
  folders: DocumentFolder[]
  selectedCount: number
  onClose: () => void
  onConfirm: (folderId: string) => void
}

export const ModalSelectFolder = ({ open, folders, selectedCount, onClose, onConfirm }: ModalSelectFolderProps) => {
  const classes = useStyles()
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)

  // モーダルを開くたびに選択をリセット
  useEffect(() => {
    if (open) {
      setSelectedFolderId(null)
    }
  }, [open])

  const handleFolderClick = useCallback((folderId: string) => {
    setSelectedFolderId(folderId)
  }, [])

  const handleConfirm = useCallback(() => {
    if (selectedFolderId) {
      onConfirm(selectedFolderId)
    }
  }, [selectedFolderId, onConfirm])

  return (
    <Modal open={open} onClose={onClose} className={classes.modal}>
      <Slide in={open} direction="up">
        <Box className={classes.container}>
          {/* ヘッダー */}
          <Box className={classes.header}>
            <Typography className={classes.title}>フォルダへ移動</Typography>
            <ButtonBase onClick={onClose} className={classes.closeButton}>
              <DynamicMuiIcon variant="close" size="20px" />
            </ButtonBase>
          </Box>

          {/* コンテンツ */}
          <Box className={classes.body}>
            <Box mb="16px" fontSize="14px" color="#707B89">
              {selectedCount}件の資料の移動先フォルダを選択してください
            </Box>
            <Box className={classes.folderList}>
              {folders.length === 0 ? (
                <Box className={classes.emptyMessage}>フォルダがありません</Box>
              ) : (
                folders.map(folder => (
                  <Box
                    key={folder.id}
                    className={`${classes.folderItem} ${
                      selectedFolderId === folder.id ? classes.folderItemSelected : ''
                    }`}
                    onClick={() => handleFolderClick(folder.id)}
                  >
                    <Box className={classes.folderItemIcon}>
                      <DynamicMuiIcon variant="folder" size="20px" />
                    </Box>
                    <Box className={classes.folderItemName}>{folder.name}</Box>
                    <Box className={classes.folderItemCount}>{folder.documentsCount}件</Box>
                  </Box>
                ))
              )}
            </Box>
          </Box>

          {/* フッター */}
          <Box className={classes.footer}>
            <Button kind="neutral" variant="outlined" title="キャンセル" onClick={onClose} />
            <Button title="移動" onClick={handleConfirm} disabled={!selectedFolderId} />
          </Box>
        </Box>
      </Slide>
    </Modal>
  )
}
